import {
  toSeoProps,
  withDefaultJsonLd,
  type SeoProps,
  type SeoRouteConfig,
  type UmbrellaSeoRoutes,
} from '@saas-pages/shared/public/seo'
import {
  buildUtilitySeoRoutes,
  type UtilityAppRegistry,
} from '@saas-pages/shared/public/platform-legacy'
import branding from '../../branding.config'
import { enTranslations } from '@/shared/i18n/en'
import { PILOT_EN_PATHS, type PilotToolPath } from '@/shared/i18n/pilot'
import { ruTranslations, type Translations } from '@/shared/i18n/ru'
import { buildTools, type ToolDefinition, type ToolKey } from '@/shared/tools/tools'

export type StaticRouteKey = 'index' | 'blog'
export type AvailableToolKey = Extract<
  ToolKey,
  | 'textDiff'
  | 'base64'
  | 'htmlEntities'
  | 'urlCodec'
  | 'utmGenerator'
  | 'jsonFormatter'
  | 'jsonMinifier'
  | 'yamlFormatter'
  | 'htmlFormatter'
  | 'javascriptFormatter'
  | 'cssFormatter'
  | 'sqlFormatter'
  | 'xmlFormatter'
  | 'csvToJson'
  | 'jsonToCsv'
  | 'uuidGenerator'
  | 'regexTester'
  | 'cronGenerator'
  | 'jwtDecoder'
  | 'textHashGenerator'
  | 'markdownEditor'
>
export type EnPilotRouteKey =
  | 'jsonFormatterEn'
  | 'jsonMinifierEn'
  | 'yamlFormatterEn'
  | 'xmlFormatterEn'
export type RouteKey = StaticRouteKey | AvailableToolKey | EnPilotRouteKey
type RuntimeRouteKey = StaticRouteKey | AvailableToolKey

interface PilotRoute {
  toolKey: Extract<
    AvailableToolKey,
    'jsonFormatter' | 'jsonMinifier' | 'yamlFormatter' | 'xmlFormatter'
  >
  ruPath: PilotToolPath
  enPath: `/en${PilotToolPath}`
  enRouteKey: EnPilotRouteKey
}

export const EN_UTILITY_PILOT: readonly PilotRoute[] = [
  {
    toolKey: 'jsonFormatter',
    ruPath: '/json-formatter',
    enPath: PILOT_EN_PATHS['/json-formatter'],
    enRouteKey: 'jsonFormatterEn',
  },
  {
    toolKey: 'jsonMinifier',
    ruPath: '/json-minifier',
    enPath: PILOT_EN_PATHS['/json-minifier'],
    enRouteKey: 'jsonMinifierEn',
  },
  {
    toolKey: 'yamlFormatter',
    ruPath: '/yaml-formatter',
    enPath: PILOT_EN_PATHS['/yaml-formatter'],
    enRouteKey: 'yamlFormatterEn',
  },
  {
    toolKey: 'xmlFormatter',
    ruPath: '/xml-formatter',
    enPath: PILOT_EN_PATHS['/xml-formatter'],
    enRouteKey: 'xmlFormatterEn',
  },
]

function buildLocaleSeoRoutes(
  t: Translations,
  pathOverrides: Partial<Record<AvailableToolKey, string>> = {},
): UmbrellaSeoRoutes<StaticRouteKey | AvailableToolKey> {
  const tools = buildTools(t).map((tool) => {
    const path = pathOverrides[tool.key as AvailableToolKey]
    return path
      ? { ...tool, path, seo: { ...tool.seo, path } }
      : tool
  })
  const registry = {
    domain: branding.domain,
    staticRoutes: [
      {
        key: 'index',
        path: '/',
        seo: {
          path: '/',
          title: t.seo.indexTitle,
          description: t.seo.indexDescription,
          changefreq: 'weekly',
          priority: 1,
        },
      },
      {
        key: 'blog',
        path: '/blog',
        seo: {
          path: '/blog',
          title: t.seo.blogTitle,
          description: t.seo.blogDescription,
          changefreq: 'weekly',
          priority: 0.8,
        },
      },
    ],
    tools: tools.filter(
      (tool): tool is ToolDefinition & { key: AvailableToolKey } =>
        tool.status === 'available',
    ),
  } satisfies UtilityAppRegistry<StaticRouteKey, ToolDefinition & { key: AvailableToolKey }>

  return withDefaultJsonLd(
    buildUtilitySeoRoutes(registry),
    'This static route has no source-owned structured data.',
  )
}

function alternates(ruPath: string, enPath: string) {
  const ru = `https://${branding.domain}${ruPath}`
  const en = `https://${branding.domain}${enPath}`
  return [
    { hreflang: 'ru', href: ru },
    { hreflang: 'en', href: en },
    { hreflang: 'x-default', href: ru },
  ] as const
}

export function buildSeoRoutes(): UmbrellaSeoRoutes<RouteKey> {
  const ruRoutes = buildLocaleSeoRoutes(ruTranslations)
  const enRoutes = buildLocaleSeoRoutes(
    enTranslations,
    Object.fromEntries(EN_UTILITY_PILOT.map((route) => [route.toolKey, route.enPath])),
  )
  const routes = { ...ruRoutes.routes } as Record<RouteKey, SeoRouteConfig>

  for (const pilot of EN_UTILITY_PILOT) {
    const routeAlternates = alternates(pilot.ruPath, pilot.enPath)
    routes[pilot.toolKey] = {
      ...ruRoutes.routes[pilot.toolKey],
      locale: 'ru',
      alternates: routeAlternates,
    }
    routes[pilot.enRouteKey] = {
      ...enRoutes.routes[pilot.toolKey],
      locale: 'en',
      alternates: routeAlternates,
    }
  }

  return { domain: branding.domain, routes }
}

export const seoRoutes: UmbrellaSeoRoutes<RouteKey> = buildSeoRoutes()

export function seoFor(
  key: RuntimeRouteKey,
  t: Translations = ruTranslations,
  pathname?: string,
): SeoProps {
  const pilot = EN_UTILITY_PILOT.find((route) => (
    route.toolKey === key && (pathname === route.ruPath || pathname === route.enPath)
  ))
  if (pilot) {
    const route = pathname === pilot.enPath
      ? seoRoutes.routes[pilot.enRouteKey]
      : seoRoutes.routes[pilot.toolKey]
    return toSeoProps(seoRoutes.domain, route, {
      locale: route.locale,
      alternates: route.alternates,
    })
  }

  const routes = buildLocaleSeoRoutes(t)
  return toSeoProps(routes.domain, routes.routes[key], {
    locale: t === enTranslations ? 'en' : 'ru',
  })
}

export function buildPilotContentExpectations() {
  return Object.fromEntries(
    EN_UTILITY_PILOT.flatMap((pilot) => [
      pilotExpectation(pilot.ruPath, pilot.toolKey, ruTranslations),
      pilotExpectation(pilot.enPath, pilot.toolKey, enTranslations),
    ]),
  )
}

function pilotExpectation(
  path: string,
  key: PilotRoute['toolKey'],
  t: Translations,
) {
  const tool = buildTools(t).find((candidate) => candidate.key === key)
  if (!tool) throw new Error(`Missing pilot tool definition: ${key}`)
  return [
    path,
    {
      h1: tool.title,
      bodyIncludes: [
        tool.longDescription,
        tool.howTo[0]!,
        ...tool.faq.map((item) => item.answer),
      ],
      bodyExcludes: t === enTranslations
        ? [
            'Upload a file',
            'Tree view',
            'JSON Schema validation',
            'Automatically repair',
            'Unlimited input',
          ]
        : [
            'Загрузите файл',
            'Дерево JSON',
            'JSON Schema validation',
            'Автоматически исправить',
            'Без ограничений',
          ],
    },
  ] as const
}
